import { emptyArray } from 'ui-components/default-props/empty';
import { STANDARD_PACKAGE_ID } from '../../../services/package-service';
import { convertDimensions } from '../../../services/unit-services/dimensions/dimension-convert-service';
import { getWeightUnitEnum } from '../../../services/unit-services/weights/weight-unit-enum-resolver';

/**
 * @typedef {import('../../../prop-types/dimenstion-unit').DimensionUnit} DimensionUnit
 * @typedef {import('./package-set').SetupV3} SetupV3
 * @typedef {import('./package-set').SetupV2} SetupV2
 * @typedef {import('./package-set').SetupPackage} SetupPackage
 * @typedef {import('./package-set').PackageSetWithJsonData} PackageSetWithJsonData
 */

/**
 * Setup V3 package set. Returns the parsed data in the correct format based
 * on the shape of the data passed in.
 * @param {object} parsedData - The parsed data to setup
 * @param {DimensionUnit} sellerDefaultDimensionsUnit - The seller's default dimensions unit
 * @returns {SetupV3} The setup package set
 */
export const setupV3PackageSet = (parsedData, sellerDefaultDimensionsUnit) => {
  const parsedPackages = parsedData?.packages ?? emptyArray;
  return parsedPackages.map((parcel) => {
    const weight = parcel.weight || {};
    weight.unit = getWeightUnitEnum(weight.unit);

    const dimensions = convertDimensions(parcel?.dimensions ?? {}, sellerDefaultDimensionsUnit);
    return {
      ...parcel,
      weight,
      dimensions,
    };
  });
};

/**
 * Setup V2 package set. Returns the parsed data in the correct format based
 * on the shape of the data passed in.
 * @param {object} parsedData - The parsed data to setup
 * @param {DimensionUnit} sellerDefaultDimensionsUnit - The seller's default dimensions unit
 * @returns {SetupV2} The setup package set
 */
export const setupV2PackageSet = (parsedData, sellerDefaultDimensionsUnit) => {
  return parsedData.map((parcel) => {
    const packages = {};
    const {
      Length: length,
      Width: width,
      Height: height,
      WeightUnitOfMeasureID: weightUnitOfMeasureID,
      Weight: weight,
      DimensionUnitOfMeasureID: unit,
    } = parcel || {};
    if (weightUnitOfMeasureID) {
      const v3Weight = {
        unit: weightUnitOfMeasureID,
        value: weight,
      };
      packages.weight = v3Weight;
    }

    const v3Dimensions = {
      length: length || 0,
      width: width || 0,
      height: height || 0,
      unit: unit || null,
    };
    packages.dimensions = convertDimensions(v3Dimensions, sellerDefaultDimensionsUnit);
    packages.packageId = STANDARD_PACKAGE_ID;
    return packages;
  });
};

/**
 * Setup package set. Returns the parsed data in the correct format based
 * on the shape of the data passed in.
 * @param {PackageSetWithJsonData} packageSet - The package set to setup
 * @param {DimensionUnit} sellerDefaultDimensionsUnit - The seller's default dimensions unit
 * @returns {SetupPackage} The setup package set
 */
export const setupPackageSet = (packageSet, sellerDefaultDimensionsUnit) => {
  const jsonData = packageSet?.jsonData;
  const parsedData = jsonData ? JSON.parse(jsonData) : {};
  if (Array.isArray(parsedData)) {
    return setupV2PackageSet(parsedData, sellerDefaultDimensionsUnit);
  }
  return setupV3PackageSet(parsedData, sellerDefaultDimensionsUnit);
};
